import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { isAddonFeature } from "@dashboard/shared/limits/utils";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import type { Draft, ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";

import { ResellerClient } from "../types";

type ClientsBuilder = EndpointBuilder<BaseQueryFn, "Clients" | "ClientDetails" | "ClientFeatures", "clientsApi">;

export type GetClientsParams = {
    workspaceId: number;
    pageIndex?: number;
    pageSize?: number;
    sortCriteria?: string;
    sortDirection?: string;
};

function getClients(builder: ClientsBuilder) {
    return builder.query<PaginatedResponse<ResellerClient>, GetClientsParams>({
        providesTags: ["Clients"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("pages:clientsOverviewPage.toast.fetchClientsError")),
        query: async ({ workspaceId, pageIndex = 0, pageSize, sortCriteria, sortDirection }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients`,
                // The table is 0-based and the backend is 1-based. Axios drops the undefined sort keys,
                // which is what keeps an unsortable column out of the query string.
                params: {
                    pageIndex: pageIndex + pageIndexOffset,
                    pageSize,
                    sortCriteria,
                    sortDirection,
                },
            };
        },
    });
}

type GetClientParams = {
    workspaceId: number;
    clientId: number;
};

function getClient(builder: ClientsBuilder) {
    return builder.query<ResellerClient, GetClientParams>({
        providesTags: ["ClientDetails"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("pages:clientsOverviewPage.toast.fetchClientError")),
        transformResponse: (response: ApiResponse<ResellerClient>) => response.model,
        query: async ({ workspaceId, clientId }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}`,
            };
        },
    });
}

type GetClientFeaturesParams = {
    workspaceId: number;
    clientId: number;
};

function getClientFeatures(builder: ClientsBuilder) {
    return builder.query<API.Feature[], GetClientFeaturesParams>({
        providesTags: ["ClientFeatures"],
        transformErrorResponse: (err) =>
            transformErrorResponse(err, i18next.t("pages:clientsOverviewPage.toast.fetchClientFeaturesError")),
        transformResponse: (response: ApiResponse<API.Feature[]>) => response.model,
        query: async ({ workspaceId, clientId }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/features`,
                params: { includeUsage: true },
            };
        },
    });
}

export type CreateClientPayload = {
    workspaceId: number;
    organizationName: string;
    licenseLimit: number;
    resellerDescription?: string;
};

type CreateClientBody = Omit<CreateClientPayload, "workspaceId">;

function createClient(builder: ClientsBuilder) {
    return builder.mutation<ResellerClient, CreateClientPayload>({
        invalidatesTags: invalidateOnSuccess("Clients"),
        transformResponse: (response: ApiResponse<ResellerClient>) => response.model,
        query: async ({ workspaceId, organizationName, licenseLimit, resellerDescription }) => {
            const base = await RouteService.getWorkspacesRoute();
            const data: CreateClientBody = { organizationName, licenseLimit };

            if (resellerDescription) {
                data.resellerDescription = resellerDescription;
            }

            return {
                url: `${base}${workspaceId}/clients`,
                method: "POST",
                data,
            };
        },
    });
}

type UpdateClientMembersPayload = {
    workspaceId: number;
    clientId: number;
    addedMembers?: Record<string, WorkspaceMember["role"]>;
    changedMembers?: Record<string, WorkspaceMember["role"]>;
    removedMembers?: string[];
};

function updateClientMembers(builder: ClientsBuilder) {
    return builder.mutation<API.Workspace, UpdateClientMembersPayload>({
        invalidatesTags: invalidateOnSuccess("ClientDetails", "ClientFeatures"),
        transformResponse: (response: ApiResponse<API.Workspace>) => response.model,
        query: async ({ workspaceId, clientId, ...data }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/members`,
                method: "PATCH",
                data,
            };
        },
    });
}

type UpdateClientExpirationPayload = {
    workspaceId: number;
    clientId: number;
    date: string;
};

function updateClientExpiration(builder: ClientsBuilder) {
    return builder.mutation<string, UpdateClientExpirationPayload>({
        invalidatesTags: invalidateOnSuccess("Clients", "ClientDetails"),
        transformResponse: (response: ApiResponse<string>) => response.model,
        query: async ({ workspaceId, clientId, date }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/expiration`,
                method: "PATCH",
                data: { date },
            };
        },
    });
}

type CancelClientSubscriptionPayload = {
    workspaceId: number;
    clientId: number;
    date: string;
};

function cancelClientSubscription(builder: ClientsBuilder) {
    return builder.mutation<string, CancelClientSubscriptionPayload>({
        invalidatesTags: invalidateOnSuccess("Clients", "ClientDetails"),
        transformResponse: (response: ApiResponse<string>) => response.model,
        query: async ({ workspaceId, clientId, date }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/cancellation`,
                method: "PATCH",
                data: { date },
            };
        },
    });
}

type ReviveClientSubscriptionPayload = {
    workspaceId: number;
    clientId: number;
};

function reviveClientSubscription(builder: ClientsBuilder) {
    return builder.mutation<string, ReviveClientSubscriptionPayload>({
        invalidatesTags: invalidateOnSuccess("Clients", "ClientDetails"),
        transformResponse: (response: ApiResponse<string>) => response.model,
        query: async ({ workspaceId, clientId }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/cancellation`,
                method: "PATCH",
                data: { date: null },
            };
        },
    });
}

type UpdateClientFeaturePayload = {
    workspaceId: number;
    clientId: number;
    type: string;
    value: number;
};

type ClientFeaturesCachePatch = { undo: () => void };
type ClientFeaturesUpdateQueryDataThunk = ThunkAction<ClientFeaturesCachePatch, unknown, unknown, UnknownAction>;

export type ClientsApiForFeatureUpdate = {
    util: {
        updateQueryData: (
            endpoint: "getClientFeatures",
            arg: GetClientFeaturesParams,
            updateFn: (draft: Draft<API.Feature[]>) => void,
        ) => ClientFeaturesUpdateQueryDataThunk;
    };
};

function updateClientFeature(builder: ClientsBuilder, getClientsApi: () => ClientsApiForFeatureUpdate) {
    return builder.mutation<API.Feature, UpdateClientFeaturePayload>({
        invalidatesTags: invalidateOnSuccess("ClientFeatures"),
        transformResponse: (response: ApiResponse<API.Feature>) => response.model,
        query: async ({ workspaceId, clientId, type, value }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}/features`,
                method: "PATCH",
                data: { type, value },
            };
        },

        async onQueryStarted({ workspaceId, clientId, type, value }, { dispatch, queryFulfilled }) {
            const patch = dispatch(
                getClientsApi().util.updateQueryData("getClientFeatures", { workspaceId, clientId }, (draft) => {
                    const feature = draft.find((item) => item.feature === type);
                    if (!feature) {
                        return;
                    }

                    if (isAddonFeature(type)) {
                        feature.used = value;
                    } else {
                        feature.max = value;
                    }
                }),
            );

            try {
                await queryFulfilled;
            } catch {
                patch.undo();
            }
        },
    });
}

type UpdateClientWorkspacePayload = {
    workspaceId: number;
    clientId: number;
    name: string;
    organizationName?: string;
    organizationVatId?: string;
    organizationUrl?: string;
    defaultGroupId?: number;
    resellerDescription?: string;
};

function updateClientWorkspace(builder: ClientsBuilder) {
    return builder.mutation<BasicApiResponse, UpdateClientWorkspacePayload>({
        invalidatesTags: invalidateOnSuccess("Clients", "ClientDetails"),
        query: async ({ workspaceId, clientId, ...data }) => {
            const base = await RouteService.getWorkspacesRoute();
            return {
                url: `${base}${workspaceId}/clients/${clientId}`,
                method: "PUT",
                data,
            };
        },
    });
}

export default {
    cancelClientSubscription,
    createClient,
    getClient,
    getClientFeatures,
    getClients,
    reviveClientSubscription,
    updateClientExpiration,
    updateClientFeature,
    updateClientMembers,
    updateClientWorkspace,
};
